// File Upload Hook

import { useState, useCallback } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { WpMediaPost } from "@components/file-display-list/types";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";

const ENDPOINT = "wp-json/wp/v2/media/";

const isWpMediaPost = (item: any): item is WpMediaPost =>
  item &&
  typeof item.id === "number" &&
  typeof item.source_url === "string" &&
  item.title &&
  typeof item.title.rendered === "string";

export const useFileUpload = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [wpFiles, setWpFiles] = useState<WpMediaPost[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [mediaError, setMediaError] = useState<string | null>(null);

  const { user } = useUserContext();
  const { getApiUrl } = useMultiSiteContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  const fetchWpFiles = useCallback(async (ids: string[]) => {
    setLoading(true);
    setMediaError(null);

    try {
      const sanitizedIds = Array.from(
        new Set(
          (ids || [])
            .map((id) => String(id).trim())
            .filter((id) => /^\d+$/.test(id)),
        ),
      );

      if (sanitizedIds.length === 0) {
        setWpFiles([]);
        setFiles([]);
        return [];
      }

      const requests = sanitizedIds.map(async (id) => {
        try {
          const res = await fetch(`${getApiUrl(ENDPOINT)}${id}`, {
            method: "GET",
          });

          if (!res.ok) {
            return null;
          }

          const data = await res.json();
          return isWpMediaPost(data) ? data : null;
        } catch {
          return null;
        }
      });

      const rawResults = await Promise.all(requests);
      const validResults = rawResults.filter((item): item is WpMediaPost => item !== null);

      if (validResults.length !== sanitizedIds.length) {
        setMediaError("Some files could not be loaded.");
      }

      setWpFiles(validResults);
      setFiles([]); // Clear local files since we're now using WP media library files

      return validResults;
    } finally {
      setLoading(false);
    }
  }, [getApiUrl]);

  // Add Files to File State
  const addFiles = (files: File[]) => {
    setFiles((prevFiles) => [...prevFiles, ...files]);
  };

  // Remove File from File State
  const removeFile = (fileName: string) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
  };

  // Clear Files from File State
  const clearFiles = () => {
    setFiles([]);
  };

  const uploadFiles = async (files: File[]) => {
    const uploads = Array.from(files).map(async (file) => {
      const formData = new FormData();

      formData.append("file", file);

      const res = await fetchWithTokenRefresh(
        `${getApiUrl(ENDPOINT)}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${user?.authToken ?? ""}`,
          },
          body: formData,
        },
        () => refreshUserToken(user?.refreshToken ?? ""),
      );

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(
          `Upload failed for ${file.name}: ${res.status} ${errText}`,
        );
      }

      return res.json();
    });

    return Promise.all(uploads);
  };

  return {
    files,
    wpFiles,
    mediaError,
    setWpFiles,
    loading,
    setLoading,
    addFiles,
    clearFiles,
    fetchWpFiles,
    removeFile,
    uploadFiles,
  };
};
